import { detectInAppBrowser, buildWayOut, nameTheApp, placeAddress } from './in-app-browser.js';

/**
 * THE ROTATE SCREEN'S WAY OUT OF AN APP'S OWN BROWSER — decided before the
 * page is painted.
 *
 * A link tapped inside Instagram, Facebook, TikTok and the like opens in the
 * app's own browser, which on a phone is locked upright. Moonfall is
 * landscape-only on a phone, so that visitor could never get past "Rotate your
 * device" (owner's iPhone, 13 October 2026). Inside such a browser the rotate
 * screen is a different card (index.html `#rotate-overlay-way-out`,
 * responsive.css): one sentence that names the app, "The Instagram browser
 * isn't supported.", and one big button, "Open in browser".
 *
 * THE CARD ASKS FOR ONE THING. The first card also printed the app-menu
 * instruction under the button. The owner, once that button had opened his
 * browser from inside Instagram on his iPhone (13 October 2026): "If the
 * button works, and it does, then we don't have to mention any other thing."
 * So the instruction is shown only when a tap did NOT leave the page, in room
 * the stylesheet keeps for it from the first paint (nothing moves when it
 * comes). Where no link is known there is no button, and the instruction is
 * the one thing on the card to do.
 *
 * WHY HERE, IN THE HEAD BUNDLE. This file rides in dist/app.pre1.js, the one
 * script in <head>, so it has run before the body is parsed. It marks <html>,
 * and the stylesheet draws the card from those marks. Decided any later (the
 * main bundle is 1.9 MB and runs at the end of the body) the screen would show
 * the ordinary text and then change under the visitor, which the game never
 * does.
 *
 *   data-in-app-browser="<app>"     a named app's browser, on a phone
 *   data-in-app-way-out="button"    a link is known to leave it
 *   data-in-app-way-out="menu"      none is: the app-menu instruction alone
 *   data-in-app-copy="pending"      the card is not ready to be seen yet
 *   data-in-app-fallback="shown"    a tap on the button left the visitor here
 *   data-in-app-copied="yes|no"     what the last press of Copy link did
 *
 * A STEP, NOT AN ERROR (17 October 2026, the paid ads). The card opens on the
 * Moonfall logo and one line of what the game is, and it has a Copy link
 * button that copies the address the visitor arrived on. Where no link is
 * known (TikTok, Snapchat, WeChat) that button is the card's big one, over the
 * app-menu instruction; where a link is known, it comes with the instruction
 * after a tap that went nowhere. The landing's Play Free is the app's way out
 * now too (landing-way-out.js); when the app swallowed THAT tap, the landing
 * leaves a note in this tab's sessionStorage and the card opens on the
 * instruction and Copy link from its first paint.
 *
 * ONE APPEARANCE. The card is unseen (`data-in-app-copy`) until it is WHOLE
 * (the parser is past its last line, the sentence has the app's name in it,
 * the link has its address), IN THE VISITOR'S LANGUAGE and DRAWN (its logo is
 * decoded, or LOGO_CAP_MS has passed: the boot veil's rule). Then it is seen,
 * once. For English that is the moment the card has been parsed, before any
 * paint can show it; for another language it is when i18n.js has written that
 * language in.
 *
 * THE ADDRESS IS TAKEN NOW AS WELL. attribution.js strips the utm tags from
 * the address bar the moment it has stored them, and the invite and voucher
 * modules do the same with their codes. What they store stays in THIS browser,
 * and the visitor is about to leave it; so the way out carries the address
 * exactly as the visitor arrived on it, every parameter included and nothing
 * added.
 *
 * AN ORDINARY BROWSER GETS NOTHING: no mark, no listener, no global. The
 * detection is one string test and this function returns.
 *
 * Which apps, what each is called and which link each is offered is
 * in-app-browser.js (pure, and the only place a marker, a name or a scheme is
 * written down).
 * Contract: tests/unit/client/inAppBrowserFlag.test.js.
 */
(function () {
  'use strict';

  /* A second evaluation (a test re-requiring the file, a hot reload) would
     leave the first one's marks and listeners on the document. */
  try {
    if (window.MoonfallInAppBrowser && typeof window.MoonfallInAppBrowser.destroy === 'function') {
      window.MoonfallInAppBrowser.destroy();
    }
  } catch (_) { /* nothing to retire */ }

  let found = null;
  try {
    found = detectInAppBrowser(window.navigator.userAgent);
  } catch (_) {
    found = null;
  }
  if (!found) return;

  const OVERLAY_ID = 'rotate-overlay';
  const SENTENCE_ID = 'rotate-overlay-way-out-body';
  const TEMPLATE_ID = 'rotate-overlay-way-out-template';
  const LINK_ID = 'rotate-overlay-way-out-link';
  const HINT_ID = 'rotate-overlay-way-out-hint';
  const STEPS_ID = 'rotate-overlay-way-out-steps';
  const LOGO_ID = 'rotate-overlay-way-out-logo';
  const COPY_ID = 'rotate-overlay-way-out-copy';
  const COPIED_ID = 'rotate-overlay-way-out-copied';
  const UNCOPIED_ID = 'rotate-overlay-way-out-uncopied';
  const UNCOPIED_TEMPLATE_ID = 'rotate-overlay-way-out-uncopied-template';
  const MARK_APP = 'data-in-app-browser';
  const MARK_WAY_OUT = 'data-in-app-way-out';
  const MARK_COPY = 'data-in-app-copy';
  const MARK_FALLBACK = 'data-in-app-fallback';
  const MARK_COPIED = 'data-in-app-copied';
  /* Left by landing-way-out.js when the app swallowed Play Free's own way out
     (this tab only). Read once, then removed. */
  const TRIED_KEY = 'moonfall_way_out_tried';

  /* i18n.js raises this on `window` once, at the end of `init`: the visitor's
     language has been fetched AND written into the page. Not its
     `moonfall:i18n-translated`, which every pass over the page's labels raises,
     including the passes other modules make before the language file has
     landed; showing the card on that one put the English on screen and then
     rewrote it (caught by rotate-prompt.spec.js, 13 October 2026). */
  const LANGUAGE_READY_EVENT = 'moonfall:i18nready';
  /* ...and that other one, on `document`, is the moment the line the sentence
     is built from may have changed: it is what the sentence is rebuilt on. */
  const LABELS_WRITTEN_EVENT = 'moonfall:i18n-translated';
  /* The longest the card is kept unseen. On a phone link the page, the bundle
     and the language file land in one to three seconds; a piece that never
     arrives must not hide the way out for good. Past this the card shows as it
     stands, in the English the page carries. */
  const HOLD_CAP_MS = 8000;
  /* How long after a tap the visitor is taken to be still here. A link that
     works takes the page off screen well inside this; one the app swallowed
     leaves the visitor looking at a button that did nothing, and this is about
     how long that takes to be noticed. */
  const FALLBACK_AFTER_MS = 2500;
  /* A timer that comes this much later than it was set for did not run late:
     the app was off screen, where timers stand still, and the visitor has come
     back. A busy phone is late by a second or two, never by this. */
  const AWAY_AFTER_MS = 5000;
  /* The longest the card waits for its logo once the card is there: the boot
     veil's own cap (index.html `#app-loading`). The file is the veil's, and
     the head preloads it at high priority, so on any ordinary visit it is
     drawn before the card is even parsed. */
  const LOGO_CAP_MS = 1500;

  let address = '';
  try {
    address = String(window.location.href);
  } catch (_) {
    address = '';
  }
  const wayOut = buildWayOut(found, address);
  if (wayOut) Object.freeze(wayOut);
  const variant = wayOut ? 'button' : 'menu';

  const root = document.documentElement;

  let overlay = null;
  let sentence = null;
  let template = null;
  let link = null;
  let logo = null;
  let copyButton = null;
  let copiedLine = null;
  let uncopiedLine = null;
  let uncopiedTemplate = null;
  /* What index.html wrote, to give back on `destroy`: each as it is taken up. */
  const written = {
    labelledBy: undefined, describedBy: undefined, sentence: undefined, href: undefined, uncopied: undefined,
  };
  let observer = null;

  /* The three things the card waits for before it is seen. */
  let whole = false;
  let worded = false;
  let pictured = false;
  let unseen = true;
  let holdTimer = null;
  let logoTimer = null;

  /* The app-menu instruction under the button. */
  let hintShown = false;
  let hintTimer = null;
  let tappedAt = 0;
  let leftSinceTap = false;

  root.setAttribute(MARK_APP, found.app);
  root.setAttribute(MARK_WAY_OUT, variant);
  root.setAttribute(MARK_COPY, 'pending');

  /* The landing already offered this app's way out, and the app swallowed
     the tap. The same button here would be the same dead end, so the card
     opens on its next step: the instruction and Copy link, from the first
     paint. (Where there is no button there is no step to skip.) */
  let tried = false;
  try {
    tried = window.sessionStorage.getItem(TRIED_KEY) === '1';
    window.sessionStorage.removeItem(TRIED_KEY);
  } catch (_) {
    tried = false;
  }
  if (tried && variant === 'button') {
    hintShown = true;
    root.setAttribute(MARK_FALLBACK, 'shown');
  }

  // --- The sentence, with the app's name in it --------------------------------

  /* index.html cannot carry the sentence: which app this is is known only
     here, and the name sits in a different place in each language. It carries
     the language's LINE instead ("The {{app}} browser isn't supported."), in
     an element that is never displayed and is a `data-i18n` label like any
     other, so i18n.js keeps it in the visitor's language. The sentence the
     visitor reads is built from that line. i18n.js never writes the sentence
     itself: it would write the line, braces and all.
     Written only when it changes, as i18n.js writes its own labels. */
  function writeSentence() {
    if (!sentence || !template) return;
    const text = nameTheApp(template.textContent, found.app);
    if (text !== null && sentence.textContent !== text) sentence.textContent = text;
  }

  /* What to type when nothing could copy: this host's /play, set into the
     language's line ("Type {{address}} into Safari or Chrome.") the way the
     app's name is set into the sentence. Written from the start, unseen, so
     the line's room under Copy link is the right height before it is needed. */
  function typedAddress() {
    try {
      return `${new URL(address).host}/play`;
    } catch (_) {
      try {
        return `${window.location.host}/play`;
      } catch (__) {
        return '';
      }
    }
  }

  function writeUncopied() {
    if (!uncopiedLine || !uncopiedTemplate) return;
    const text = placeAddress(uncopiedTemplate.textContent, typedAddress());
    if (text !== null && uncopiedLine.textContent !== text) uncopiedLine.textContent = text;
  }

  /* Every pass i18n.js makes may have changed the lines both are built from. */
  function writeBuilt() {
    writeSentence();
    writeUncopied();
  }

  /* The dialog is named by the sentence and described by whatever else there
     is on the card to read: the instruction where there is no button; with a
     button, nothing until the instruction is shown. (A line that is laid out
     and unseen would still be read out if it were named here.) */
  function describeDialog() {
    if (!overlay) return;
    const by = variant === 'menu' ? STEPS_ID : (hintShown ? HINT_ID : null);
    if (by) overlay.setAttribute('aria-describedby', by);
    else overlay.removeAttribute('aria-describedby');
  }

  // --- One appearance: whole, and in its final language ------------------------

  /**
   * The language this page is about to show, as the head's own language script
   * resolved it (index.html `moonfall-i18n-preload`: the link's `?lang=`, then
   * the saved choice, then English). It left its request, and that request's
   * address names the language. Null when it cannot be read.
   */
  function bootLanguage() {
    try {
      const request = window.__moonfallTranslationRequest;
      const match = request && typeof request.url === 'string'
        ? /\/ui\.([A-Za-z-]+)\.json(?:[?#]|$)/.exec(request.url)
        : null;
      return match ? match[1] : null;
    } catch (_) {
      return null;
    }
  }

  function letSee() {
    if (!unseen) return;
    unseen = false;
    window.removeEventListener(LANGUAGE_READY_EVENT, languageOn);
    if (holdTimer !== null) {
      clearTimeout(holdTimer);
      holdTimer = null;
    }
    root.removeAttribute(MARK_COPY);
  }

  function show() {
    if (whole && worded && pictured) letSee();
  }

  function languageOn() {
    worded = true;
    // The lines the built ones are made from are in their language now.
    writeBuilt();
    show();
  }

  // --- The logo, drawn before the card is seen ---------------------------------

  function logoDrawn() {
    if (logoTimer !== null) {
      clearTimeout(logoTimer);
      logoTimer = null;
    }
    if (logo) {
      logo.removeEventListener('load', logoLoaded);
      logo.removeEventListener('error', logoDrawn);
    }
    if (pictured) return;
    pictured = true;
    show();
  }

  /* Loaded is not yet drawn: the picture is decoded first, as the boot veil's
     is, so the card never shows an empty box where the logo goes. */
  function logoLoaded() {
    try {
      if (logo && typeof logo.decode === 'function') {
        logo.decode().then(logoDrawn, logoDrawn);
        return;
      }
    } catch (_) { /* drawn as it is */ }
    logoDrawn();
  }

  /* A picture already complete is drawn (or broken, which is as final). */
  function watchLogo() {
    if (logo.complete) {
      logoDrawn();
      return;
    }
    logo.addEventListener('load', logoLoaded);
    logo.addEventListener('error', logoDrawn);
    logoTimer = setTimeout(logoDrawn, LOGO_CAP_MS);
  }

  /* Past the cap the card is shown as far as it has come. What is there is
     named and armed now, and the parser is still watched: whatever arrives
     later is taken up as it comes. */
  function giveUp() {
    holdTimer = null;
    takeUp();
    letSee();
  }

  // --- Copy link ---------------------------------------------------------------

  /* The address goes on the clipboard the way every page has done it from a
     tap: selected in a text box of its own and copied, in the same turn as the
     tap (an app's web view may have no clipboard API, or refuse it). The box
     is read-only, so no keyboard opens; fixed at the top, so nothing scrolls;
     16px, so iOS does not zoom to it; inside the dialog, which is the one part
     of the page the rotate guard leaves focusable. */
  function copyBySelection(text) {
    const holder = copyButton && copyButton.parentNode;
    if (!holder) return false;
    const box = document.createElement('textarea');
    box.value = text;
    box.setAttribute('readonly', '');
    box.setAttribute('aria-hidden', 'true');
    box.tabIndex = -1;
    box.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;margin:0;padding:0;border:0;'
      + 'opacity:0;font-size:16px;pointer-events:none;';
    holder.appendChild(box);
    let copied = false;
    try {
      box.select();
      box.setSelectionRange(0, text.length);
      copied = document.execCommand('copy') === true;
    } catch (_) {
      copied = false;
    }
    box.remove();
    try {
      const selection = window.getSelection();
      if (selection) selection.removeAllRanges();
    } catch (_) { /* nothing selected */ }
    if (document.activeElement !== copyButton) {
      try {
        copyButton.focus({ preventScroll: true });
      } catch (_) { /* the visitor's place is wherever the browser left it */ }
    }
    return copied;
  }

  /* Says what the press did. The line is in a live region: written again
     (the same words) so a screen reader hears it on every press. */
  function copied(worked) {
    root.setAttribute(MARK_COPIED, worked ? 'yes' : 'no');
    const line = worked ? copiedLine : uncopiedLine;
    if (line) {
      const text = line.textContent;
      line.textContent = text;
    }
  }

  function copyLink() {
    const text = address;
    if (!text) {
      copied(false);
      return;
    }
    if (copyBySelection(text)) {
      copied(true);
      return;
    }
    let writing = null;
    try {
      const clipboard = window.navigator.clipboard;
      if (clipboard && typeof clipboard.writeText === 'function') writing = clipboard.writeText(text);
    } catch (_) {
      writing = null;
    }
    if (!writing || typeof writing.then !== 'function') {
      copied(false);
      return;
    }
    writing.then(() => copied(true), () => copied(false));
  }

  /* index.html carries the English, letter for letter what ui.json holds, so
     an English card is final as parsed. Any other language is written in by
     i18n.js when the bundle has run; a language that cannot be known might be
     one of those. */
  worded = bootLanguage() === 'en';
  if (!worded) window.addEventListener(LANGUAGE_READY_EVENT, languageOn);
  holdTimer = setTimeout(giveUp, HOLD_CAP_MS);

  // --- The button, and the instruction a tap that went nowhere brings ----------

  /* Facebook on iPhone passes the Safari scheme on only when it is opened in a
     new window from the tap (in-app-browser.js). The link keeps its address, so
     it is still a link to a keyboard, a screen reader and a long press; if the
     window cannot even be asked, the link is left to go by itself. */
  function openInWindow(event) {
    try {
      window.open(wayOut.href, '_blank');
    } catch (_) {
      return;
    }
    event.preventDefault();
  }

  function onScreen() {
    try {
      return document.visibilityState === 'visible';
    } catch (_) {
      return true;
    }
  }

  /* None of the links is documented by the app that honours it, and an update
     can end any of them. The page cannot ask whether a tap worked; it can only
     notice that the visitor is still looking at it. A tap that worked took the
     page off screen: it was hidden, or its timers stood still. Only when
     neither happened is the instruction shown, and then it stays. */
  function stillHere() {
    hintTimer = null;
    const late = Date.now() - tappedAt - FALLBACK_AFTER_MS;
    if (leftSinceTap || !onScreen() || late > AWAY_AFTER_MS) return;
    hintShown = true;
    root.setAttribute(MARK_FALLBACK, 'shown');
    describeDialog();
  }

  /* A second tap while the first is being waited on neither hurries the
     instruction nor puts it off; the link itself goes again every time. */
  function noteTap() {
    if (hintShown || hintTimer !== null) return;
    leftSinceTap = false;
    tappedAt = Date.now();
    hintTimer = setTimeout(stillHere, FALLBACK_AFTER_MS);
  }

  function noteLeaving() {
    if (!onScreen()) leftSinceTap = true;
  }

  // --- The overlay, as soon as it is parsed whole -------------------------------

  /* The parser is past the card's last line: that line is in the document and
     something follows it, there or further up; or the document is done. Until
     then a line of the card may be missing or half written (a slow link
     delivers the page in pieces, and the browser paints between them), and a
     card shown then would grow and move as the rest arrived.
     The look starts at the last line, inside the card, where only the parser
     writes: what follows it there is the markup's own line break. (Starting at
     the overlay would be fooled by any script that adds an element to the end
     of the body while the overlay is still arriving.) */
  function parsedWhole() {
    if (document.readyState !== 'loading') return true;
    /** @type {Node | null} */
    let node = document.getElementById(STEPS_ID);
    for (; node && node !== root; node = node.parentNode) {
      if (node.nextSibling) return true;
    }
    return false;
  }

  /* Names and arms as much of the card as is in the document. Each piece is
     taken up once; calling again picks up what has arrived since. */
  function takeUp() {
    if (!overlay) {
      overlay = document.getElementById(OVERLAY_ID);
      if (!overlay) return;
      written.labelledBy = overlay.getAttribute('aria-labelledby');
      written.describedBy = overlay.getAttribute('aria-describedby');
    }
    if (!sentence) {
      sentence = document.getElementById(SENTENCE_ID);
      if (sentence) {
        written.sentence = sentence.textContent;
        overlay.setAttribute('aria-labelledby', SENTENCE_ID);
        document.addEventListener(LABELS_WRITTEN_EVENT, writeBuilt);
      }
    }
    if (!template) template = document.getElementById(TEMPLATE_ID);
    if (!logo) {
      logo = document.getElementById(LOGO_ID);
      if (logo) watchLogo();
    }
    if (!copyButton) {
      copyButton = document.getElementById(COPY_ID);
      if (copyButton) copyButton.addEventListener('click', copyLink);
    }
    if (!copiedLine) copiedLine = document.getElementById(COPIED_ID);
    if (!uncopiedLine) {
      uncopiedLine = document.getElementById(UNCOPIED_ID);
      if (uncopiedLine) written.uncopied = uncopiedLine.textContent;
    }
    if (!uncopiedTemplate) uncopiedTemplate = document.getElementById(UNCOPIED_TEMPLATE_ID);
    writeBuilt();
    describeDialog();
    if (!link && wayOut) {
      link = document.getElementById(LINK_ID);
      if (link) {
        written.href = link.getAttribute('href');
        link.setAttribute('href', wayOut.href);
        if (wayOut.open === 'window') link.addEventListener('click', openInWindow);
        link.addEventListener('click', noteTap);
        document.addEventListener('visibilitychange', noteLeaving);
      }
    }
  }

  /** Once the card is whole: names it, arms its link and lets it be seen. */
  function arm() {
    if (whole) return true;
    if (!document.getElementById(OVERLAY_ID) || !parsedWhole()) return false;
    takeUp();
    whole = true;
    // A card written without a logo has none to wait for.
    if (!logo) pictured = true;
    show();
    return true;
  }

  function stopWatching() {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    document.removeEventListener('DOMContentLoaded', lastLook);
  }

  function lastLook() {
    arm();
    stopWatching();
  }

  /* The head runs before the body exists. Watch the parser until it is past
     the card's last line, arm the card in the same turn (so it is whole before
     it can be painted), then stop: nothing is observed after that. */
  if (!arm()) {
    if (typeof MutationObserver === 'function') {
      observer = new MutationObserver(function () {
        if (arm()) stopWatching();
      });
      observer.observe(root, { childList: true, subtree: true });
    }
    document.addEventListener('DOMContentLoaded', lastLook);
  }

  function giveBack(element, name, value) {
    if (value === null) element.removeAttribute(name);
    else element.setAttribute(name, value);
  }

  function destroy() {
    stopWatching();
    letSee();
    if (hintTimer !== null) {
      clearTimeout(hintTimer);
      hintTimer = null;
    }
    pictured = true;
    logoDrawn();
    window.removeEventListener(LANGUAGE_READY_EVENT, languageOn);
    document.removeEventListener(LABELS_WRITTEN_EVENT, writeBuilt);
    document.removeEventListener('visibilitychange', noteLeaving);
    root.removeAttribute(MARK_APP);
    root.removeAttribute(MARK_WAY_OUT);
    root.removeAttribute(MARK_FALLBACK);
    root.removeAttribute(MARK_COPIED);
    if (copyButton) copyButton.removeEventListener('click', copyLink);
    if (uncopiedLine && written.uncopied !== undefined && uncopiedLine.textContent !== written.uncopied) {
      uncopiedLine.textContent = written.uncopied;
    }
    if (overlay) {
      if (written.labelledBy !== undefined) giveBack(overlay, 'aria-labelledby', written.labelledBy);
      if (written.describedBy !== undefined) giveBack(overlay, 'aria-describedby', written.describedBy);
    }
    if (sentence && written.sentence !== undefined && sentence.textContent !== written.sentence) {
      sentence.textContent = written.sentence;
    }
    if (link) {
      link.removeEventListener('click', openInWindow);
      link.removeEventListener('click', noteTap);
      if (written.href !== undefined) giveBack(link, 'href', written.href);
    }
    overlay = null;
    sentence = null;
    template = null;
    link = null;
    logo = null;
    copyButton = null;
    copiedLine = null;
    uncopiedLine = null;
    uncopiedTemplate = null;
    whole = true;
    try {
      delete window.MoonfallInAppBrowser;
    } catch (_) { /* a frozen window keeps it; it is inert now */ }
  }

  /* Read by the tests, and by anything that needs to know where the page is.
     Frozen so a page cannot rewrite the answer. */
  window.MoonfallInAppBrowser = Object.freeze({
    app: found.app,
    os: found.os,
    wayOut: wayOut,
    HOLD_CAP_MS: HOLD_CAP_MS,
    FALLBACK_AFTER_MS: FALLBACK_AFTER_MS,
    AWAY_AFTER_MS: AWAY_AFTER_MS,
    LOGO_CAP_MS: LOGO_CAP_MS,
    destroy: destroy,
  });
}());
